import { Spacer } from "@hackthenorth/north";
import { Button, Checkbox, Text } from "north.js";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useQueryParam } from "use-query-params";

import {
  Divider,
  errorToast,
  Header,
  PageWrapper,
  successToast,
} from "src/shared/components";
import Search from "src/shared/components/Hardware/Search";
import { EVENT_ID } from "src/shared/constants/event";
import { globalConstants } from "src/theme/globalConstants";

import { useGetEventAttendeesForUsersQuery } from "../graphql/getEventAttendeesForUsers.generated";
import { useGetUsersByNameLazyQuery } from "../graphql/getUserByName.generated";
import { useSetEventAttendeeHasIdMutation } from "../graphql/setEventAttendeeHasId.generated";

import RequestsTab from "./requestsTab";

const UserTab = () => {
  function useDebounce(cb: string, delay: number) {
    const [debounceValue, setDebounceValue] = useState(cb);
    useEffect(() => {
      const handler = setTimeout(() => {
        setDebounceValue(cb);
      }, delay);

      return () => {
        clearTimeout(handler);
      };
    }, [cb, delay]);
    return debounceValue;
  }

  const [query, setQuery] = useQueryParam<string>("search");
  const [debouncedQuery, setDebouncedQuery] = useState<string>("");
  const debounceQuery = useDebounce(query, 300);
  const [users, setUsers] = useState<
    { name: string; email: string; id: number }[]
  >([]);
  const [getUsersByName, { data, loading }] = useGetUsersByNameLazyQuery();
  const [selectedUser, setSelectedUser] = useState<{
    name: string;
    email: string;
    id: number;
  } | null>(null);
  const [tab, setTab] = useState<"users" | "requests">("users");

  const onSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.currentTarget.value === "") {
        setQuery("");
      } else {
        setQuery(e.currentTarget.value);
      }
    },
    [setQuery]
  );
  useEffect(() => {
    setDebouncedQuery(query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceQuery]);

  useEffect(() => {
    if (debouncedQuery === "") {
      setUsers([]);
      return;
    }

    const fetchUsers = async () => {
      try {
        await getUsersByName({
          variables: { search: debouncedQuery, event_id: EVENT_ID },
        });
      } catch (error) {
        console.error("Error fetching users:", error);
        setUsers([]);
      }
    };

    fetchUsers();
  }, [debouncedQuery, getUsersByName]);

  useEffect(() => {
    if (data && data.users) {
      const formattedUsers = data.users.map((user) => ({
        name: user.name,
        email: user.email,
        id: user.id,
      }));
      setUsers(formattedUsers);
    } else {
      setUsers([]);
    }
  }, [data]);

  const userIds = useMemo(() => users.map((user) => user.id), [users]);

  const { data: eventAttendeesData, refetch: refetchEventAttendees } =
    useGetEventAttendeesForUsersQuery({
      variables: { user_ids: userIds, event_id: EVENT_ID },
      skip: userIds.length === 0,
    });

  const eventAttendeeByUserId = useMemo(() => {
    const map = new Map<number, { id: number; has_id: boolean }>();
    eventAttendeesData?.eventAttendees.forEach((attendee) => {
      map.set(attendee.user_id, { id: attendee.id, has_id: attendee.has_id });
    });
    return map;
  }, [eventAttendeesData]);

  const [setEventAttendeeHasId, { loading: isUpdatingHasId }] =
    useSetEventAttendeeHasIdMutation();

  const handleHasIdChange = useCallback(
    async (userName: string, eventAttendeeId: number, hasId: boolean) => {
      try {
        await setEventAttendeeHasId({
          variables: { event_attendee_id: eventAttendeeId, has_id: hasId },
        });
        await refetchEventAttendees();
        successToast(
          hasId
            ? `Marked ${userName}'s ID as collected`
            : `Marked ${userName}'s ID as not on file`
        );
      } catch (error) {
        errorToast("Failed to update ID status");
      }
    },
    [setEventAttendeeHasId, refetchEventAttendees]
  );

  if (tab === "requests" && selectedUser) {
    return (
      <RequestsTab
        userId={selectedUser.id}
        onBack={() => setTab("users")}
        name={selectedUser.name}
      />
    );
  }

  return (
    <>
      <Header
        title="Hardware"
        subtitle="Browse and request a variety of hardware to bring your project to life!"
      />
      <PageWrapper>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            marginBottom: "48px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            <Text type="h2">Users</Text>
            <Text
              type="body1"
              style={{ color: globalConstants?.color?.textSecondary as string }}
            >
              Search for users to view their hardware requests and manage their
              inventory.
            </Text>
          </div>
          <FlexRow>
            <SearchWrapper>
              <Search
                query={query || ""}
                onSearchChange={onSearchChange}
                totalSearchResults={users.length}
                placeholder="Search by name or email"
              />
            </SearchWrapper>
          </FlexRow>
        </div>
        {!loading &&
          users.map((user, index) => {
            const eventAttendee = eventAttendeeByUserId.get(user.id);

            return (
              <div
                style={{ display: "flex", flexDirection: "column" }}
                key={user.email}
              >
                {index !== 0 && <Divider style={{ margin: "24px 0" }} />}
                <Bold type="body1">{user.name}</Bold>
                <Spacer height={8} />
                <EmailText type="body1">{user.email}</EmailText>
                <Spacer height={19} />
                <ActionsRow>
                  <Button
                    onClick={() => {
                      setSelectedUser(user);
                      setTab("requests");
                    }}
                  >
                    See requests
                  </Button>
                  {eventAttendee ? (
                    <Checkbox
                      checked={eventAttendee.has_id}
                      disabled={isUpdatingHasId}
                      label="ID on file"
                      onChange={(e) =>
                        handleHasIdChange(
                          user.name,
                          eventAttendee.id,
                          e.target.checked
                        )
                      }
                    />
                  ) : (
                    <EmailText type="body2">No hacker record</EmailText>
                  )}
                </ActionsRow>
              </div>
            );
          })}
      </PageWrapper>
    </>
  );
};

const EmailText = styled(Text)`
  color: ${globalConstants?.color?.textSecondary};
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 24px */
`;

const Bold = styled(Text)`
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 160%; /* 25.6px */
`;

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const SearchWrapper = styled.div`
  width: 100%;
`;

export default UserTab;
